<!-- ============================================
     IMAGE MODE - Simplified poster-based modal
     Toggle: USE_IMAGE_MODE in component.ts
     ============================================ -->
<ng-container *ngIf="useImageMode">
  <div class="quinny-campaign-image-mode">
    <!-- Close Button -->
    <button type="button" class="image-mode-close-btn" (click)="onClose()" aria-label="Close modal">
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path d="M14 4L4 14M4 4L14 14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
      </svg>
    </button>
    
    <!-- Campaign Poster Image -->
    <img [src]="campaignPosterUrl" alt="Quinny AI Campaign" class="campaign-poster-image" width="600" height="400" />
    
    <!-- CTA Button Overlay -->
    <button type="button" class="image-mode-cta-button" (click)="onTryNow()">
      <span>TRY NOW</span>
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </button>
  </div>
</ng-container>

<!-- ============================================
     DETAILED MODE - Full interactive modal (for rollback)
     Toggle: USE_IMAGE_MODE in component.ts
     ============================================ -->
<div class="quinny-campaign-container" *ngIf="!useImageMode">
  <!-- Left Column - Light Theme -->
  <div class="left-column">
    <!-- Early Access Badge -->
    <div class="early-access-badge">
      <span class="badge-icon">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path d="M12 2L14.4 8.2L21 9.2L16 14L17.5 21L12 17.8L6.5 21L8 14L3 9.2L9.6 8.2L12 2Z" fill="currentColor"/>
        </svg>
      </span>
      <span class="badge-text">You've Been Selected for <span class="highlight">EARLY ACCESS!</span></span>
    </div>

    <!-- Introducing Section -->
    <div class="introducing-section">
      <span class="intro-text">Introducing</span>
      <div class="quinny-brand">
        <div class="quinny-logo-small">
          <img src="https://cdn.qne.cloud/qca/assets/img/quinny-ai-logo.webp" alt="Quinny AI" width="120" height="40" />
        </div>
      </div>
    </div>

    <!-- Main Headline -->
    <h1 class="main-headline">
      <span class="gradient-text">The AI Behind Your Numbers</span>
    </h1>

    <!-- Descriptive Text -->
    <div class="description-text">
      <p class="intro-paragraph">You're among selected users with early access. Ask questions. Get instant insights from your business data.</p>
      
      <p class="feature-text">No reports. No guesswork. <span class="feature-highlight">Just clear answers.</span></p>
    </div>

    <!-- CTA Button -->
    <button type="button" class="cta-button" (click)="onTryNow()">
      <span>TRY NOW</span>
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </button>
  </div>

  <!-- Right Column - Dark Theme -->
  <div class="right-column">
    <!-- Ambient Background Effects -->
    <div class="ambient-glow glow-1"></div>
    <div class="ambient-glow glow-2"></div>
    <div class="ambient-glow glow-3"></div>
    
    <!-- Floating Particles -->
    <div class="particle particle-1"></div>
    <div class="particle particle-2"></div>
    <div class="particle particle-3"></div>

    <!-- Close Button -->
    <button type="button" class="close-btn" (click)="onClose()" aria-label="Close modal">
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path d="M14 4L4 14M4 4L14 14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
      </svg>
    </button>

    <!-- Floating Feature Icons -->
    <div class="floating-icons" aria-hidden="true">
      <!-- Top area icons -->
      <div class="feature-icon icon-calculator">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <rect x="4" y="2" width="16" height="20" rx="2" stroke="currentColor" stroke-width="1.5"/>
          <rect x="6" y="4" width="12" height="4" rx="1" fill="currentColor" fill-opacity="0.3"/>
          <circle cx="8" cy="12" r="1" fill="currentColor"/>
          <circle cx="12" cy="12" r="1" fill="currentColor"/>
          <circle cx="16" cy="12" r="1" fill="currentColor"/>
          <circle cx="8" cy="16" r="1" fill="currentColor"/>
          <circle cx="12" cy="16" r="1" fill="currentColor"/>
          <circle cx="16" cy="16" r="1" fill="currentColor"/>
        </svg>
      </div>
      
      <div class="feature-icon icon-analytics">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <rect x="4" y="2" width="16" height="20" rx="2" stroke="currentColor" stroke-width="1.5"/>
          <path d="M8 14L11 11L14 13L18 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      
      <div class="feature-icon icon-search">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <circle cx="11" cy="11" r="7" stroke="currentColor" stroke-width="1.5"/>
          <path d="M16 16L20 20" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
      </div>

      <!-- Additional icons - positioned around edges -->
      <div class="feature-icon icon-chart">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M3 3V21H21" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M7 16L12 11L15 14L21 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>

      <div class="feature-icon icon-wallet">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <rect x="2" y="6" width="20" height="14" rx="2" stroke="currentColor" stroke-width="1.5"/>
          <path d="M22 10H18C16.8954 10 16 10.8954 16 12C16 13.1046 16.8954 14 18 14H22" stroke="currentColor" stroke-width="1.5"/>
          <path d="M6 6V4C6 2.89543 6.89543 2 8 2H16C17.1046 2 18 2.89543 18 4V6" stroke="currentColor" stroke-width="1.5"/>
        </svg>
      </div>

      <div class="feature-icon icon-pie">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.5"/>
          <path d="M12 2V12L19 19" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
      </div>
    </div>

    <!-- Chat Demo Interface -->
    <div class="chat-demo">
      <!-- User Message with Avatar -->
      <div class="user-message-container">
        <div class="message-bubble user-bubble">
          <p>Which items have the highest profit margin?</p>
        </div>
        <div class="avatar user-avatar">
          <img src="https://cdn.qne.cloud/qca/assets/img/quinny-ai/user-avatar.png" alt="User" width="40" height="40" />
        </div>
      </div>

      <!-- AI Response -->
      <div class="message-row ai-message">
        <div class="ai-avatar-wrapper">
          <img [src]="quinnYAiAvatarUrl" alt="Quinny AI" width="40" height="40" />
        </div>
        <div class="ai-response-content">
          <span class="ai-label">Quinny AI</span>
          <span class="ai-status">
            Analyzing your data
            <span class="typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </span>
          </span>
        </div>
      </div>

      <!-- Chat Input Preview -->
      <div class="chat-input-preview">
        <span class="placeholder-text">Talk to Quinny AI</span>
        <button type="button" class="send-btn" aria-label="Send message">
          <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
            <path d="M18 2L9 11M18 2L12 18L9 11M18 2L2 8L9 11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
    </div>
  </div>
</div>
